// ============================================================
// frontend/src/components/UI.tsx
// ============================================================
// Shared, reusable UI primitives: Modal, ModalFooter, Field,
// Section, Stat, StatusPill, PaymentPill.
// ============================================================

import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import type { ClientPackageWithStats, SessionStatus } from '@consilium/shared';

// ----- Modal: centered on desktop, bottom sheet on mobile -----
interface ModalProps {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}
export function Modal({ title, children, onClose, wide }: ModalProps) {
  return (
    <div
      className="modal-backdrop"
      // Close only when the press *starts* on the backdrop. Using `onClick`
      // here would also fire when a drag inside an input (e.g. selecting
      // text) releases on the backdrop, dismissing the modal mid-edit.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`modal-content ${wide ? 'wide' : ''}`}>
        <div className="row" style={{ marginBottom: 20 }}>
          <h2 className="display" style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
            {title}
          </h2>
          <button className="btn-icon" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ModalFooter({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: 'flex', gap: 8, justifyContent: 'flex-end',
      marginTop: 24, paddingTop: 20,
      borderTop: '1px solid var(--border)',
    }}>
      {children}
    </div>
  );
}

// ----- Field: form label + input -----
interface FieldProps {
  label: string;
  children: ReactNode;
  required?: boolean;
  hint?: string;
}
export function Field({ label, children, required, hint }: FieldProps) {
  return (
    <div>
      <label className="label">
        {label}
        {required && <span style={{ color: 'var(--danger)', marginLeft: 4 }}>*</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// ----- Section: page section with title + content card -----
interface SectionProps {
  title: string;
  count?: number;
  children: ReactNode;
  action?: ReactNode;
}
export function Section({ title, count, children, action }: SectionProps) {
  return (
    <section style={{ marginBottom: 28 }}>
      <div className="row" style={{ marginBottom: 12, paddingLeft: 4 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <h2 className="section-title">{title}</h2>
          {count !== undefined && <span style={{ fontSize: 13, color: 'var(--muted-strong)' }}>{count}</span>}
        </div>
        {action}
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>{children}</div>
    </section>
  );
}

// ----- Stat: label + big number -----
export function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="mono-cap" style={{ marginBottom: 6 }}>{label}</div>
      <div className="display" style={{ fontSize: 28, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.03em', lineHeight: 1 }}>
        {value}
      </div>
    </div>
  );
}

// ----- StatusPill: colored badge for session status -----
const STATUS_MAP: Record<SessionStatus, { className: string; text: string }> = {
  scheduled:  { className: 'pill-scheduled', text: 'Scheduled' },
  completed:  { className: 'pill-completed', text: 'Completed' },
  cancelled:  { className: 'pill-cancelled', text: 'Cancelled' },
  'no-show':  { className: 'pill-noshow',    text: 'No-show' },
};
export function StatusPill({ status }: { status: SessionStatus }) {
  const x = STATUS_MAP[status] ?? STATUS_MAP.scheduled;
  return <span className={`pill ${x.className}`}>{x.text}</span>;
}

// ----- PaymentPill: paid / partial / unpaid -----
// Expects pkg with paidTotal and totalFee (both as numbers).
export function PaymentPill({ pkg }: { pkg: Pick<ClientPackageWithStats, 'paidTotal' | 'totalFee'> }) {
  const paid = Number(pkg.paidTotal || 0);
  const total = Number(pkg.totalFee || 0);
  if (paid >= total) return <span className="pill pill-paid">Paid</span>;
  if (paid > 0) return <span className="pill pill-partial">{paid.toLocaleString()} / {total.toLocaleString()}</span>;
  return <span className="pill pill-unpaid">Unpaid</span>;
}
